import React, { useState } from 'react'

import moment from 'moment'

import Modal from '@components/common/modal/Modal'
import { Checkbox } from '@components/common/form'
import { YSpacing, FlexContainer, Loader } from '@components/common'
import Table from '@components/common/Table'
import Button from '@components/common/form/Button'

import { useMutation, useQuery } from 'react-query'

import services from '@services'
const { RestService } = services

import {
  AccountFutureOrders,
  GOLocation,
  UpdateAccountFutureOrdersAddressesReq,
  UpdateAccountFutureOrdersAddressesRes,
} from '@types'

interface AccountFutureOrdersAndGOLocationsEditModalProps {
  accountId: string
  headquarterId: number
  addressId: string
  userEmail?: string
  opsEmail?: string

  close: () => void
  successFlashAlert: (message: string) => void
  errorFlashAlert: (message: string) => void
}

const AccountFutureOrdersAndGOLocationsEditModal = ({
  accountId,
  headquarterId,
  addressId,
  userEmail,
  opsEmail,
  close,
  successFlashAlert,
  errorFlashAlert,
}: AccountFutureOrdersAndGOLocationsEditModalProps) => {
  const [selectedGOLocationIds, setSelectedGOLocationIds] = useState<string[]>(
    [],
  )
  const [selectedCateringOrderIds, setSelectedCateringOrderIds] = useState<
    string[]
  >([])
  const [selectedGroupOrderIds, setSelectedGroupOrderIds] = useState<string[]>(
    [],
  )

  const futureOrdersMutation = useMutation(
    async (
      req: UpdateAccountFutureOrdersAddressesReq,
    ): Promise<UpdateAccountFutureOrdersAddressesRes> => {
      const res = await RestService.put(`/accounts/future_orders`, req)

      return res
    },
    {
      onSuccess: (res: UpdateAccountFutureOrdersAddressesRes) => {
        if (res.hasErrors) {
          errorFlashAlert(
            'There were some errors updating future orders and Group order locations. Please contact support for details.',
          )
        } else {
          successFlashAlert(
            'Future orders and Group orders locations have been successfully updated. Please check your inbox for details.',
          )
        }
        close()
      },
      onError: () => {
        errorFlashAlert(
          'There was an error updating future orders and Group order locations. Please try again or contact support.',
        )
      },
    },
  )

  const futureOrdersQuery = useQuery(
    ['futureOrders', accountId, headquarterId, addressId],
    async (): Promise<AccountFutureOrders> => {
      const params = { hqId: headquarterId, addressId }
      const futureOrders = await RestService.get(
        `/accounts/${accountId}/future_orders`,
        params,
        { timeout: 30000 },
      )

      return futureOrders
    },
  )

  const goLocationsQuery = useQuery(
    ['golocations', accountId, headquarterId, addressId],
    async (): Promise<GOLocation[]> => {
      const params = { addressId, hqId: headquarterId }
      const res = await RestService.get(
        `/accounts/${accountId}/go_locations`,
        params,
        { timeout: 30000 },
      )

      return res
    },
  )

  const isLoading = futureOrdersQuery.isLoading || goLocationsQuery.isLoading
  const cateringOrders = futureOrdersQuery?.data?.cateringOrders || []
  const groupOrders = futureOrdersQuery?.data?.groupOrders || []
  const goLocations = goLocationsQuery?.data || []

  const isSaveDisabled =
    isLoading ||
    (selectedCateringOrderIds.length === 0 &&
      selectedGroupOrderIds.length === 0 &&
      selectedGOLocationIds.length === 0)

  const handleChangeGOLocationSelection = (locationId: string) => {
    setSelectedGOLocationIds((prevSelected) => {
      if (prevSelected.includes(locationId)) {
        return prevSelected.filter((id) => id !== locationId)
      } else {
        return [...prevSelected, locationId]
      }
    })
  }

  const isGOLocationSelected = (locationId: string) => {
    return selectedGOLocationIds.includes(locationId)
  }

  const handleChangeCateringOrderSelection = (orderId: string) => {
    setSelectedCateringOrderIds((prevSelected) => {
      if (prevSelected.includes(orderId)) {
        return prevSelected.filter((id) => id !== orderId)
      } else {
        return [...prevSelected, orderId]
      }
    })
  }

  const isCateringOrderSelected = (orderNumber: string) => {
    return selectedCateringOrderIds.includes(orderNumber)
  }

  const handleChangeGroupOrderSelection = (orderId: string) => {
    setSelectedGroupOrderIds((prevSelected) => {
      if (prevSelected.includes(orderId)) {
        return prevSelected.filter((id) => id !== orderId)
      } else {
        return [...prevSelected, orderId]
      }
    })
  }

  const isGroupOrderSelected = (orderNumber: string) => {
    return selectedGroupOrderIds.includes(orderNumber)
  }

  const renderCateringOrdersTable = () => {
    return (
      <Table headings={['Order Number', 'Date', 'Update Address']}>
        {cateringOrders.map((order) => {
          return (
            <tr key={order.orderNumber}>
              <td>{order.orderNumber}</td>
              <td>{moment(order.date).format('MM/DD/YYYY')}</td>
              <td>
                <Checkbox
                  checked={isCateringOrderSelected(order.orderId)}
                  onChange={() =>
                    handleChangeCateringOrderSelection(order.orderId)
                  }
                />
              </td>
            </tr>
          )
        })}
      </Table>
    )
  }

  const renderGroupOrdersTable = () => {
    return (
      <Table headings={['Order Number', 'Date', 'Update Address']}>
        {groupOrders.map((order) => {
          return (
            <tr key={order.orderNumber}>
              <td>{order.orderNumber}</td>
              <td>{moment(order.date).format('MM/DD/YYYY')}</td>
              <td>
                <Checkbox
                  checked={isGroupOrderSelected(order.orderId)}
                  onChange={() =>
                    handleChangeGroupOrderSelection(order.orderId)
                  }
                />
              </td>
            </tr>
          )
        })}
      </Table>
    )
  }

  const renderNoOrders = (ordersType: string) => {
    if (isLoading) {
      return <Loader />
    }

    return (
      <FlexContainer
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <p>
          No {ordersType} future orders where an Address would need to be
          updated.
        </p>
      </FlexContainer>
    )
  }

  const onUpdate = async () => {
    const emails = [userEmail, opsEmail].filter(
      (email): email is string => email !== undefined && email !== '',
    )

    const req: UpdateAccountFutureOrdersAddressesReq = {
      hqId: headquarterId,
      accountId,
      emails: emails,
      dropoffAddressId: addressId,
      selectedCateringOrders: selectedCateringOrderIds,
      selectedGroupOrders: selectedGroupOrderIds,
      selectedGOLocs: selectedGOLocationIds,
    }

    futureOrdersMutation.mutate(req)
  }

  return (
    <Modal
      title={'Update Future Orders And Group Order Locations'}
      height="700px"
      hideModal={close}
    >
      <FlexContainer
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <h1 style={{ fontSize: '16px', padding: '4px' }}>
          There has been an address change for this account - please select all
          future orders and group order locations in which to apply this address
          update. If you do not wish to update the address on future orders,
          simply press close.
        </h1>
        <YSpacing height="15px" />
        <h2 style={{ fontSize: '16px', fontWeight: 'bold', padding: '4px' }}>
          Future Catering Orders
        </h2>
        {cateringOrders.length > 0
          ? renderCateringOrdersTable()
          : renderNoOrders('catering')}
        <YSpacing height="15px" />
        <h2 style={{ fontSize: '16px', fontWeight: 'bold', padding: '4px' }}>
          Future Group Orders
        </h2>
        {groupOrders.length > 0
          ? renderGroupOrdersTable()
          : renderNoOrders('group')}
        <YSpacing height="15px" />
        <h2 style={{ fontSize: '16px', fontWeight: 'bold', padding: '4px' }}>
          Group Order Locations
        </h2>
        <Table headings={['Location Name', 'Update Location']}>
          {goLocations.map((location) => {
            return (
              <tr key={location.locationId}>
                <td>{location.name}</td>
                <td>
                  <Checkbox
                    checked={isGOLocationSelected(location.locationId)}
                    onChange={() =>
                      handleChangeGOLocationSelection(location.locationId)
                    }
                  />
                </td>
              </tr>
            )
          })}
        </Table>
        <YSpacing height="10px" />
      </FlexContainer>
      <FlexContainer
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
      >
        {isLoading && <Loader />}
        {!isLoading && (
          <Button
            margin="0px 10px 0px 0px"
            label="Close"
            onClick={close}
            width="30%"
          />
        )}
        {!isLoading && (
          <Button
            disabled={isSaveDisabled}
            label="Save"
            onClick={onUpdate}
            width="30%"
          />
        )}
      </FlexContainer>
    </Modal>
  )
}

export default AccountFutureOrdersAndGOLocationsEditModal
